import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

interface ArtifactEmptyStateProps {
  variant: 'no-artifacts' | 'no-results' | 'error';
  onRetry?: () => void;
  onClearFilters?: () => void;
  searchQuery?: string;
  typeFilter?: string;
}

export const ArtifactEmptyState = ({
  variant,
  onRetry,
  onClearFilters,
  searchQuery,
  typeFilter,
}: ArtifactEmptyStateProps) => {
  const baseClasses =
    'flex flex-col items-center justify-center h-full w-full p-8 text-center';

  switch (variant) {
    case 'no-artifacts':
      return (
        <div className={baseClasses}>
          <div className="bg-muted p-4 rounded-full mb-4">
            <MagnifyingGlassIcon className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            No artifacts yet
          </h3>
          <p className="text-muted-foreground max-w-md">
            This matter doesn&apos;t have any artifacts yet. They&apos;ll appear
            here as documents are uploaded or calls are processed.
          </p>
        </div>
      );

    case 'no-results':
      return (
        <div className={baseClasses}>
          <div className="bg-muted p-4 rounded-full mb-4">
            <MagnifyingGlassIcon className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            No matching artifacts
          </h3>
          <p className="text-muted-foreground mb-4 max-w-md">
            {searchQuery && typeFilter && typeFilter !== 'all'
              ? `No artifacts match "${searchQuery}" in ${typeFilter.toLowerCase()} type.`
              : searchQuery
                ? `No artifacts match "${searchQuery}".`
                : typeFilter && typeFilter !== 'all'
                  ? `No ${typeFilter.toLowerCase()} artifacts found.`
                  : 'No artifacts match your current filter criteria.'}
          </p>
          {onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="px-4 py-2 text-sm bg-muted text-foreground rounded-md hover:bg-muted/80 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      );

    case 'error':
      return (
        <div className={baseClasses}>
          <div className="bg-destructive/10 text-destructive p-3 rounded-full mb-4">
            <ArrowPathIcon className="h-6 w-6" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            Failed to load artifacts
          </h3>
          <p className="text-muted-foreground mb-4 max-w-md">
            There was an error loading the artifacts for this matter. Please try
            again.
          </p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              aria-label="Retry loading artifacts"
            >
              Try Again
            </button>
          )}
        </div>
      );

    default:
      return null;
  }
};
